import { ApiHelper, ApiHelperWithData, ApiResponseWithData, BaseRequest, splitParams } from '..';
import type {
  ApndFileInfo,
  ApndUploadedFile,
  MpMgmtSearchParams,
  MpMgmtSearchResponse,
  MstMemberDetailResponse,
} from '../model';

export interface MpGradeCareerPeriod {
  from: string;
  to: string;
}

export interface MpGradeEducation {
  acbgCd: string;
  graduationDate: string;
  graduationStatus: string;
}

export interface MpGradeCalculateParams {
  psClsfCd: string;
  employmentStartDate: string;
  careers: MpGradeCareerPeriod[];
  educations: MpGradeEducation[];
  cqlfCd: string;
  cqlfAcqDd: string;
}

export interface MpGradeCalculateResult {
  totalCareer?: number;
  totalCareerMonths?: number;
  totalCareerText?: string;
  gradeCode?: string;
  gradeName?: string;
  finalGrade?: number;
  appliedBasis?: string;
  recognizedCareerDays?: number;
  careerText?: string;
  firstCareerDate?: string;
  lastCareerDate?: string;
  isRegularEmployee?: boolean;
  employmentStartDate?: string;
  employmentCareerDays?: number;
  preEmploymentCareerDays?: number;
  totalRecognizedCareerDays?: number;
  totalRecognizedCareerText?: string;
  educationCandidates?: unknown[];
  certificateCandidates?: unknown[];
  combinationCandidates?: unknown[];
  hasOverlapCareer?: boolean;
  needConfirm?: boolean;
  confirmReason?: string;
}

export interface MpProjectCareerRoleSaveParams {
  empId: string;
  prjtCrerSq: number;
  roleCd: string;
}

export interface MpProjectCareerSkillSaveParams {
  empId: string;
  prjtCrerSq: number;
  cdType: string;
  cd: string;
}

export interface MpProjectCareerSaveParams {
  empId: string;
  prjtCrerSq: number;
  prjtNm: string;
  thrwDd: string;
  endDd: string;
  cs: string;
  rol: string;
  //tsk: string;
  os: string;
  databaseName: string;
  roles: MpProjectCareerRoleSaveParams[];
  skills: MpProjectCareerSkillSaveParams[];
}

export interface MpMgmtEditCareerRoleDetail {
  roleCd?: string;
  roleNm?: string;
  rolCd?: string;
  cd?: string;
}

export interface MpMgmtEditCareerSkillDetail {
  cdType?: string;
  cd?: string;
  cdNm?: string;
  skillCd?: string;
  code?: string;
}

export interface MpMgmtEditSaveParams {
  empId: string;
  gdCd: string;
  totCrer: string;
  rcmmCd: string;
  thrwPosbDd: string;
  careers: MpProjectCareerSaveParams[];
  files: ApndUploadedFile[];
}

export interface MpMgmtEditDeleteParams {
  empId: string;
}

export interface MpMgmtEditCareerDetail {
  projectName?: string;
  prjtNm?: string;
  prjt_nm?: string;
  client?: string;
  cs?: string;
  role?: string;
  rol?: string;
  roleCd?: string;
  roleCodes?: string;
  task?: string;
  tsk?: string;
  from?: string;
  thrwDd?: string;
  thrw_dd?: string;
  to?: string;
  endDd?: string;
  end_dd?: string;
  os?: string;
  db?: string;
  databaseName?: string;
  database_name?: string;
  langTool?: string;
  skillCodes?: string;
  skills?: string | MpMgmtEditCareerSkillDetail[];
  roles?: MpMgmtEditCareerRoleDetail[];
}

export interface MpMgmtEditManagementRecord {
  content?: string;
  mgHst?: string;
  managementHistory?: string;
  managementContent?: string;
  date?: string;
  wrtDd?: string;
  createdAt?: string;
  regDttm?: string;
  RegDttm?: string;
  managementDate?: string;
  author?: string;
  regrId?: string;
  RegrId?: string;
  regrNm?: string;
  wrtrNm?: string;
  writer?: string;
}

export interface MpMgmtEditManagementInfo {
  currentProject?: string;
  currentProjectName?: string;
  availableDate?: string;
  thrwPosbDd?: string;
  firstCareerDate?: string;
  employeeType?: string;
  employeeTypeName?: string;
  managementRecords?: MpMgmtEditManagementRecord[];
  mgHstList?: MpMgmtEditManagementRecord[];
}

export interface MpMgmtEditDetail {
  member?: MstMemberDetailResponse;
  careerSummaryList?: MpMgmtEditCareerDetail[];
  careers?: MpMgmtEditCareerDetail[];
  managementInfo?: MpMgmtEditManagementInfo;
  managementRecords?: MpMgmtEditManagementRecord[];
  mgHstList?: MpMgmtEditManagementRecord[];
  files?: ApndFileInfo[];
}
/**
 * clover framework 코드 API
 */
export class MpMgmtEditApi {
  private withData: ApiHelperWithData;

  constructor(private helper: ApiHelper) {
    this.withData = new ApiHelperWithData(helper);
  }

  /**
   * 그리드 목록 조회
   */
  callFunction = (
    params: {
      code: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/apis/mp-edit/search';
    return this.withData.postJson(url, ...splitParams(params));
  };

  detail = (
    params: { empId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<{ detail?: MpMgmtEditDetail } & MpMgmtEditDetail>> => {
    const url = '/apis/mp-edit/detail';
    return this.withData.postJson(url, ...splitParams(params));
  };

  search = (
    params: MpMgmtSearchParams & BaseRequest,
  ): Promise<ApiResponseWithData<MpMgmtSearchResponse>> => {
    const url = '/apis/mp-mgmt/search';
    return this.withData.postJson(url, ...splitParams(params));
  };

  calculateGrade = (
    params: MpGradeCalculateParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: MpGradeCalculateResult }>> => {
    const url = '/apis/mp-edit/grade/calculate';
    return this.withData.postJson(url, ...splitParams(params));
  };

  save = (
    params: MpMgmtEditSaveParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/apis/mp-edit/save';
    return this.withData.postJson(url, ...splitParams(params));
  };

  update = (
    params: MpMgmtEditSaveParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/apis/mp-edit/update';
    return this.withData.postJson(url, ...splitParams(params));
  };

  delete = (
    params: MpMgmtEditDeleteParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/apis/mp-edit/delete';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 파일 업로드
  uploadFile = (
    params: {
      file: Blob;
      fileName: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<ApndUploadedFile>> => {
    const [data, extra] = splitParams(params);
    const formData = new FormData();
    formData.append('file', data.file, data.fileName);
    const url = '/apis/mp-edit/upload';
    return this.withData.postMultipart(url, formData, extra);
  };

  // 파일 삭제 (임시 파일 정리)
  deleteFile = (
    params: {
      fileId: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<void>> => {
    const url = '/apis/mp-edit/delete-file';
    return this.withData.postJson(url, ...splitParams(params));
  };
}
